import {
  createMassTortSurvey,
  createMassTortSurveyDistribution,
  createMassTortSurveyQuestion,
  deleteMassTortSurvey,
  deleteMassTortSurveyQuestion,
  listMassTortSurveyDistributionItems,
  listMassTortSurveyQuestions,
  listMassTortSurveys,
  reorderMassTortSurveyQuestions,
  retrieveMassTortSurveyDistribution,
  updateMassTortSurvey,
  updateMassTortSurveyQuestion,
} from "@/lib/data/requests/mass-torts/mass-tort-survey";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { queryKeys } from "@/lib/data/static/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const surveyListKey = (massTortId: string) => [
  queryKeys.massTortSurveys,
  massTortId,
];
const questionKey = (surveyId: string) => [
  queryKeys.massTortSurveys,
  surveyId,
  "questions",
];

export const useMassTortSurveys = (massTortId: string, pageNumber: number) =>
  useQuery({
    queryKey: [...surveyListKey(massTortId), pageNumber],
    queryFn: () => listMassTortSurveys(massTortId, pageNumber),
    enabled: Boolean(massTortId),
  });

export const useMassTortSurveyQuestions = (surveyId: string) =>
  useQuery({
    queryKey: questionKey(surveyId),
    queryFn: () => listMassTortSurveyQuestions(surveyId),
    enabled: Boolean(surveyId),
  });

export const useCreateMassTortSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMassTortSurvey,
    onSuccess: async (_response, input) => {
      await queryClient.invalidateQueries({
        queryKey: surveyListKey(input.massTortId),
      });
    },
  });
};

export const useUpdateMassTortSurvey = (massTortId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMassTortSurvey,
    onSuccess: async (_response, input) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: surveyListKey(massTortId),
        }),
        queryClient.invalidateQueries({
          queryKey: questionKey(input.surveyId),
        }),
      ]);
    },
  });
};

export const useDeleteMassTortSurvey = (massTortId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMassTortSurvey,
    onSuccess: async (_response, surveyId) => {
      queryClient.removeQueries({ queryKey: questionKey(surveyId) });
      await queryClient.invalidateQueries({
        queryKey: surveyListKey(massTortId),
      });
    },
  });
};

export const useCreateMassTortSurveyQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMassTortSurveyQuestion,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.massTortSurveys],
      });
    },
  });
};

export const useUpdateMassTortSurveyQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMassTortSurveyQuestion,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.massTortSurveys],
      });
    },
  });
};

export const useDeleteMassTortSurveyQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMassTortSurveyQuestion,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.massTortSurveys],
      });
    },
  });
};

export const useReorderMassTortSurveyQuestions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderMassTortSurveyQuestions,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.massTortSurveys],
      });
    },
  });
};

export const useCreateMassTortSurveyDistribution = () =>
  useMutation({ mutationFn: createMassTortSurveyDistribution });

export const useMassTortSurveyDistribution = (requestId: string) =>
  useQuery({
    queryKey: [queryKeys.massTortSurveyDistributions, requestId],
    queryFn: () => retrieveMassTortSurveyDistribution(requestId),
    enabled: Boolean(requestId),
    refetchInterval: (query) => {
      const state = query.state.data?.data.state;
      return state === "completed" || state === "completed_with_errors"
        ? false
        : 3000;
    },
  });

export const useMassTortSurveyDistributionItems = (
  requestId: string,
  enabled: boolean,
  pageNumber: number,
) =>
  useQuery({
    queryKey: [
      queryKeys.massTortSurveyDistributions,
      requestId,
      "items",
      pageNumber,
    ],
    queryFn: () => listMassTortSurveyDistributionItems(requestId, pageNumber),
    enabled: Boolean(requestId) && enabled,
  });

export const useMassTortSurveyMatterTitles = (matterIds: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [queryKeys.matters, "mass-tort-survey-titles", matterIds],
    enabled: matterIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const uniqueIds = [...new Set(matterIds)];
      const titles: Record<string, string> = {};
      let nextIndex = 0;

      // Resolve titles without flooding the API when a result page has 50 matters.
      await Promise.all(
        Array.from({ length: Math.min(3, uniqueIds.length) }, async () => {
          while (nextIndex < uniqueIds.length) {
            const matterId = uniqueIds[nextIndex++];
            try {
              const response = await queryClient.ensureQueryData(
                retrieveMatterQueryOptions(matterId),
              );
              const matter = response.data;
              if (typeof matter.matterTitle === "string") {
                titles[matterId] =
                  matter.matterTitle.trim() || "Untitled matter";
              }
            } catch {
              // An inaccessible matter still has its distribution outcome.
            }
          }
        }),
      );
      return titles;
    },
  });
};
